'use client';

import Link from '@carvana/ds-link';
import { CornerUpRight, MapPin, Phone } from 'lucide-react';
import { Fragment } from 'react';
import DsModal from '@/common/DsModal';
import GoogleMap from '~/components/dealership/GoogleMap';
import ShiftDigitalEventName from '~/constants/dealership/ShiftDigitalEventName';
import { useModalContext } from '~/context/dealership/ModalContext';
import {
  useDealershipAddress,
  useDealershipDepartmentHours,
  useDealershipPhones
} from '~/context/dealership/StaticContext';
import useGoogleMapsDirection from '~/hooks/dealership/useGoogleMapsDirection';
import toLowerKebabCase from '~/utilities/toLowerKebabCase';
import {
  ContactContainer,
  Heading,
  HoursWrapper,
  IconWrapper,
  Label,
  Line,
  MapWrapper,
  Row,
  RowWrapper
} from './ContactModal.styles';

const linkText = 'Get directions';

export default function ContactModal() {
  const address = useDealershipAddress();
  const departmentHours = useDealershipDepartmentHours();
  const phones = useDealershipPhones();
  const googleMapsDirections = useGoogleMapsDirection();
  const { isContactModalOpen, setContactModalOpen } = useModalContext();

  return (
    <DsModal
      classes={{
        container: 'max-h-[95%]'
      }}
      data-testid="contact-modal"
      isOpen={isContactModalOpen}
      onClose={() => setContactModalOpen(false)}
      data-analytics-id="Contact Modal"
      desktopWidth="640px"
      mobileStyle="inset"
    >
      <div className="flex flex-col gap-3xl sm:gap-4xl">
        <div className="flex flex-col gap-2xl">
          <div className="flex flex-col gap-lg">
            <Heading className="t-heading-lg sm:t-heading-xl">
              Our location
            </Heading>

            <div className="flex flex-col gap-lg sm:gap-xs">
              <ContactContainer>
                <IconWrapper>
                  <MapPin size={16} />
                </IconWrapper>
                <p className="self-center text-text-default t-body-sm">
                  {address.street1}
                  <br className="sm:hidden" /> {address.city}, {address.state}{' '}
                  {address.zip}
                </p>
              </ContactContainer>

              <ContactContainer>
                <IconWrapper>
                  <Phone size={16} />
                </IconWrapper>
                <div className="flex flex-col self-center sm:flex-row sm:gap-lg">
                  {phones.map((phone, index) => (
                    <Fragment key={phone.label}>
                      {index > 0 && <Line />}

                      <p>
                        <Label>{phone.label}: </Label>
                        <Link
                          href={`tel:${phone.number}`}
                          size="sm"
                          preventVisitedColor
                          data-adobe-id={`click-to-call-${phone.label.toLowerCase()}`}
                          data-analytics-id="Phone number"
                          data-anlytics-attributes={JSON.stringify(phone)}
                          data-sda-event={ShiftDigitalEventName.ClickToCall}
                          data-sda-event-set-prop={`clickToCallDepartment|${phone.label}`}
                          data-testid="phone-number"
                        >
                          {phone.number}
                        </Link>
                      </p>
                    </Fragment>
                  ))}
                </div>
              </ContactContainer>
            </div>
          </div>

          <div className="flex flex-col gap-md">
            <MapWrapper>
              <GoogleMap center={address.position} zoom={10} />
            </MapWrapper>
            <Link
              data-adobe-id={toLowerKebabCase(linkText)}
              data-analytics-id="Contact Modal"
              data-anlytics-attributes={JSON.stringify({
                item: linkText,
                section: 'Contact Modal'
              })}
              data-sda-event={ShiftDigitalEventName.GetDirections}
              className="flex flex-row items-center gap-md text-text-primary"
              href={googleMapsDirections}
              preventVisitedColor
              target="_blank"
              rel="noopener noreferrer"
              variant="standalone"
              size="sm"
              data-testid="get-directions"
            >
              <CornerUpRight size={16} />
              {linkText}
            </Link>
          </div>
        </div>

        <div className="flex flex-col gap-lg sm:gap-xl">
          <Heading className="t-heading-lg">Hours</Heading>

          <div className="flex w-full flex-col gap-lg sm:flex-row sm:gap-4xl">
            {departmentHours.map(department => (
              <HoursWrapper key={department.label}>
                <Label>{department.label}</Label>
                <RowWrapper>
                  {department.daysHours.map(({ days, hours }) => (
                    <Row key={`${department.label}-${days}`}>
                      <span>{days}</span>
                      <span>{hours}</span>
                    </Row>
                  ))}
                </RowWrapper>
              </HoursWrapper>
            ))}
          </div>
        </div>
      </div>
    </DsModal>
  );
}
